import { colors, fonts, fmt } from './UI'
import Hint from './Hint'

// ============================================================================
// Investing-Readiness Coach
// ----------------------------------------------------------------------------
// PHILOSOPHY: this informs, it does not instruct. It shows the user where they
// stand against targets THEY set, and surfaces relevant facts (e.g. what their
// debt costs) so they can decide for themselves. It must never:
//   - recommend a specific investment, product, platform, or amount to invest
//   - tell the user to invest, or when to invest
//   - cross from general guidance into a personal recommendation
// The finish line is "you've hit the milestones people generally aim for before
// investing - a good time to start LEARNING", pointing at the Learn section.
//
// ⚠️ COMPLIANCE: all user-facing copy lives in the COPY block below so it can be
// reviewed/edited in one place. Get a proper FCA compliance review before this
// goes to paying users. This is general guidance, not regulated financial advice.
// ============================================================================

// All user-facing strings in one place for easy compliance review/editing.
const COPY = {
  buffer: {
    title: 'Build a starter buffer',
    info: (have, target) => `You've got ${fmt(have)} set aside. Many people aim for a small starter buffer of around £1,000 before anything else, so a surprise cost doesn't become debt.`,
  },
  debt: {
    title: 'Understand what your debt costs',
    info: (annualInterest, topApr) => `Your debts are costing roughly ${fmt(annualInterest)} a year in interest${topApr ? ` (highest rate ${topApr}% APR)` : ''}. Worth knowing: few mainstream investments reliably beat high interest rates, so many people choose to clear expensive debt first. Your call.`,
  },
  emergency: {
    title: 'Reach your emergency fund target',
    info: (months, target) => `You're covered for about ${months.toFixed(1)} of the ${target} months you're aiming for. An emergency fund is the cushion that lets you handle the unexpected without derailing everything else.`,
  },
  savings: {
    title: 'Hit your savings rate',
    info: (rate, target) => `You're saving ${Math.round(rate)}% of your income against your own ${target}% target. A steady savings habit is what frees up money to put to work later.`,
  },
  ready: {
    title: "You've hit your foundations",
    info: "You've reached the milestones people generally aim for before they start investing - a healthy buffer, manageable debt, and a steady savings rate. A good moment to start learning about what comes next.",
    cta: 'Explore the Learn section →',
  },
  disclaimer: 'General guidance based on your own targets - not financial advice.',
}

// Evaluate the user's position. Returns the current focus + progress.
// Signals are things the dashboard already computes; targets are user-set.
export function evaluateReadiness({
  emergencyBalance = 0,
  emergencyFundMonths = 0,
  emergencyTargetMonths = 6,
  debts = [],
  totalDebt = 0,
  savingsRate = 0,
  savingsRateTarget = 20,
}) {
  const STARTER_BUFFER = 1000

  // Annual interest cost across all debts (information, not judgement)
  const annualInterest = debts.reduce((s, d) => s + (d.current_balance || 0) * ((d.apr || 0) / 100), 0)
  const topApr = debts.reduce((m, d) => Math.max(m, d.apr || 0), 0)
  const hasExpensiveDebt = topApr > 0 && annualInterest > 0

  // The ladder, in order. Each step is "done" or not; the first not-done is the
  // current focus. Targets are the user's own where applicable.
  const steps = [
    {
      key: 'buffer',
      title: COPY.buffer.title,
      done: emergencyBalance >= STARTER_BUFFER,
      info: COPY.buffer.info(emergencyBalance, STARTER_BUFFER),
      progress: Math.min(1, emergencyBalance / STARTER_BUFFER),
    },
    {
      key: 'debt',
      title: COPY.debt.title,
      // "Done" here just means there's no expensive debt to inform about - we
      // never assert they MUST clear it, only surface the cost while it exists.
      done: !hasExpensiveDebt,
      info: COPY.debt.info(annualInterest, topApr ? Math.round(topApr) : 0),
      progress: hasExpensiveDebt ? 0.5 : 1,
    },
    {
      key: 'emergency',
      title: COPY.emergency.title,
      done: emergencyFundMonths >= emergencyTargetMonths,
      info: COPY.emergency.info(emergencyFundMonths, emergencyTargetMonths),
      progress: emergencyTargetMonths > 0 ? Math.min(1, emergencyFundMonths / emergencyTargetMonths) : 1,
    },
    {
      key: 'savings',
      title: COPY.savings.title,
      done: savingsRate >= savingsRateTarget,
      info: COPY.savings.info(savingsRate, savingsRateTarget),
      progress: savingsRateTarget > 0 ? Math.min(1, savingsRate / savingsRateTarget) : 1,
    },
  ]

  const doneCount = steps.filter(s => s.done).length
  const currentIndex = steps.findIndex(s => !s.done)
  const ready = currentIndex === -1

  // Are any rungs AHEAD of the current one already satisfied? If so we
  // acknowledge it lightly ("already sorted") without breaking the one-step
  // framing - the user still works the ladder in order, but gets credit.
  const aheadAlreadyMet = !ready
    ? steps.slice(currentIndex + 1).filter(s => s.done).map(s => s.title)
    : []

  return {
    steps,
    doneCount,
    total: steps.length,
    ready,
    current: ready ? null : steps[currentIndex],
    currentStepNumber: ready ? steps.length : currentIndex + 1,
    aheadAlreadyMet,
  }
}

export default function ReadinessCoach({ signals, onNavigate }) {
  const r = evaluateReadiness(signals)

  return (
    <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden', marginBottom: '14px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 12px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, display: 'flex', alignItems: 'center', gap: '6px' }}>YOUR PATH TO INVESTING <Hint title="Path to Investing" text="Three key steps before putting money into investments: build an emergency fund, clear high-interest debt, and hit a consistent savings rate. Tick all three and you're ready to invest." /></div>
        {!r.ready && <div style={{ fontSize: '13px', fontWeight: 600, color: colors.gold }}>Step {r.currentStepNumber} of {r.total}</div>}
      </div>

      <div style={{ padding: '0 16px 16px' }}>
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: '5px', marginBottom: '16px' }}>
          {r.steps.map((s, i) => {
            const isCleared = i < r.currentStepNumber - 1
            const isCurrent = i === r.currentStepNumber - 1
            return (
              <div key={s.key} style={{ flex: 1, height: '4px', borderRadius: '2px', background: isCleared ? colors.gold : isCurrent ? 'rgba(201,151,43,0.45)' : 'rgba(255,255,255,0.08)' }} />
            )
          })}
        </div>

        {r.ready ? (
          <div>
            <div style={{ fontFamily: fonts.heading, fontSize: '18px', fontWeight: 700, color: colors.green, marginBottom: '6px' }}>{COPY.ready.title}</div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.6, marginBottom: '14px' }}>{COPY.ready.info}</div>
            <button onClick={() => onNavigate && onNavigate('learn')} style={{ background: colors.gold, border: 'none', borderRadius: '12px', color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, padding: '12px 20px', cursor: 'pointer', width: '100%' }}>
              {COPY.ready.cta}
            </button>
          </div>
        ) : (
          <div>
            {r.steps.map((s, i) => {
              const isDone = s.done
              const isCurrent = i === r.currentStepNumber - 1
              const isLocked = i > r.currentStepNumber - 1 && !s.done
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px', opacity: isLocked ? 0.4 : 1 }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, marginTop: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', background: isDone ? colors.green : isCurrent ? 'rgba(201,151,43,0.2)' : 'rgba(255,255,255,0.07)', border: isDone ? 'none' : isCurrent ? `1px solid ${colors.gold}` : '1px solid rgba(255,255,255,0.15)', color: isDone ? '#fff' : isCurrent ? colors.gold : colors.muted, fontWeight: 700 }}>
                    {isDone ? '✓' : i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: isDone ? colors.green : isCurrent ? colors.text : colors.muted, marginBottom: isCurrent ? '4px' : 0 }}>{s.title}</div>
                    {isCurrent && <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, lineHeight: 1.6 }}>{s.info}</div>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div style={{ fontSize: '12px', fontWeight: 500, color: `rgba(${colors.textRgb},0.35)`, padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.05)', lineHeight: 1.5 }}>
        {COPY.disclaimer}
      </div>
    </div>
  )
}
